import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

export const ArenaOpsInvariantIdSchema = z.enum([
  /** A — every balance equals its newest movement snapshot. */
  "BALANCE_VS_SNAPSHOT",
  /** B — LOCK and RELEASE net to zero across the ledger. */
  "LOCK_RELEASE_NETS_TO_ZERO",
  /** C — `amount` equals the snapshot delta. */
  "AMOUNT_VS_SNAPSHOT_DELTA",
  /**
   * The closure check: A + B − C must account for the ENTIRE gap between what
   * the ledger declares and what the balance table holds. Three green invariants
   * prove only the absence of the three breach kinds someone thought to look
   * for; this one fails when a FOURTH kind exists that none of them detects. Its
   * `breachUsdt` is the unexplained residual, and it names no movements — by
   * definition it fires when we cannot say which movements are responsible.
   */
  "INVARIANTS_EXPLAIN_IDENTITY_GAP",
]);

export const ArenaOpsInvariantSchema = z.object({
  id: ArenaOpsInvariantIdSchema,
  passed: z.boolean(),
  breachCount: z.number().int(),
  breachUsdt: ArenaOpsUsdtSchema,
  offendingMovementIds: z.array(z.number().int()),
});

/**
 * Reported as-is, never filtered out. `FREE_TIER_ENTRIES_CHARGED` is an open
 * product question — entries marked free that carry a charge — and excluding
 * them in SQL would make the question unrecoverable. `DEPOSIT_TABLE_COVERAGE`
 * records that `CedeArenaDeposit` covers a minority of deposits, so it is a
 * reconciliation signal, not a cash-in source.
 */
export const ArenaOpsDataQualityLineSchema = z.object({
  id: z.enum(["FREE_TIER_ENTRIES_CHARGED", "DEPOSIT_TABLE_COVERAGE", "ANCHOR_SET_EXCLUDED_LEGACY_USERS"]),
  label: z.string(),
  count: z.number().int(),
  amountUsdt: ArenaOpsUsdtSchema.nullable(),
  /** Decimal string ratio where the line is a coverage figure, else null. */
  ratio: z.string().nullable(),
});

export const ArenaOpsOverviewReconciliationResponseSchema = z.object({
  invariants: z.array(ArenaOpsInvariantSchema),
  dataQuality: z.array(ArenaOpsDataQualityLineSchema),
});
export type ArenaOpsOverviewReconciliationResponse = z.infer<typeof ArenaOpsOverviewReconciliationResponseSchema>;
